import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Shield,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Calendar,
  Upload,
  Trash2,
  Plus,
  X,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  ArrowLeft,
  Save,
  Camera,
  Tag,
  Award,
  Zap,
  Eye,
  EyeOff,
  Lock,
  ChevronRight,
  AlertCircle,
  BadgeCheck,
  Sparkles,
  Brain,
  ExternalLink,
  Star,
  Edit
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Expertise categories for the tag system
const EXPERTISE_CATEGORIES = [
  { id: "dialysis_water", label: "Dialysis Water Engineer", color: "bg-blue-100 text-blue-700" },
  { id: "master_plumber", label: "Master Plumber", color: "bg-cyan-100 text-cyan-700" },
  { id: "hvac_med", label: "HVAC-Med", color: "bg-purple-100 text-purple-700" },
  { id: "medical_gas", label: "Medical Gas Certified", color: "bg-emerald-100 text-emerald-700" },
  { id: "ro_specialist", label: "RO Specialist", color: "bg-indigo-100 text-indigo-700" },
  { id: "electrical", label: "Electrical Contractor", color: "bg-amber-100 text-amber-700" },
  { id: "general_contractor", label: "General Contractor", color: "bg-slate-100 text-slate-700" },
  { id: "sterilization", label: "Sterilization Systems", color: "bg-teal-100 text-teal-700" },
  { id: "water_purification", label: "Water Purification", color: "bg-sky-100 text-sky-700" },
  { id: "biomedical", label: "Biomedical Equipment", color: "bg-rose-100 text-rose-700" },
  { id: "fire_safety", label: "Fire & Safety Systems", color: "bg-red-100 text-red-700" },
  { id: "elevator", label: "Elevator/Lift Specialist", color: "bg-orange-100 text-orange-700" }
];

// Credential type options
const CREDENTIAL_TYPES = [
  { value: "license", label: "License" },
  { value: "certification", label: "Certification" },
  { value: "coi", label: "Certificate of Insurance (COI)" },
  { value: "bond", label: "Bond" },
  { value: "training", label: "Training Certificate" },
  { value: "other", label: "Other" }
];

export default function ContractorProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("identity");
  
  // Profile data
  const [profile, setProfile] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    website: "",
    notes: "",
    company_logo: "",
    owner_photo: "",
    expertise_tags: [],
    primary_category: "",
    google_business_url: ""
  });
  
  // AI Profile state
  const [aiProfile, setAiProfile] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [websiteUrlInput, setWebsiteUrlInput] = useState("");
  const [googleBusinessInput, setGoogleBusinessInput] = useState("");
  const [editingAiProfile, setEditingAiProfile] = useState(false);
  const [editedCapabilities, setEditedCapabilities] = useState("");
  const [deletingAiProfile, setDeletingAiProfile] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [editingImages, setEditingImages] = useState(false);
  const [savingImages, setSavingImages] = useState(false);
  
  // Credentials/Vault
  const [credentials, setCredentials] = useState([]);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newCredential, setNewCredential] = useState({
    type: "license",
    name: "",
    issuing_body: "",
    expiry_date: "",
    coverage_amount: "",
    file_url: "",
    notes: ""
  });
  
  // COI specific state
  const [coiStatus, setCoiStatus] = useState({
    has_valid_coi: false,
    coi_expiry: null,
    days_until_expiry: null,
    is_verified: false
  });
  
  // Compliance status
  const [complianceStatus, setComplianceStatus] = useState({
    is_discoverable: false,
    expiring_soon: [],
    expired: [],
    all_valid: true
  });
  
  // Password change
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Welcome banner dismissed
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);
  
  const token = localStorage.getItem("contractor_token");

  useEffect(() => {
    if (!token) {
      navigate("/contractor/login");
      return;
    }
    fetchProfile();
  }, [navigate, token]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/contractor/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { contractor: {...} } or { profile: {...} }
      const data = res.data.contractor || res.data.profile || res.data;
      
      setProfile({
        company_name: data.company_name || "",
        contact_name: data.contact_name || data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        zip_code: data.zip_code || "",
        website: data.website || data.website_url || "",
        notes: data.notes || "",
        company_logo: data.company_logo || "",
        owner_photo: data.owner_photo || "",
        expertise_tags: data.expertise_tags || data.categories || [],
        primary_category: data.primary_category || "",
        google_business_url: data.google_business_url || ""
      });
      
      // Set AI profile data
      if (data.ai_profile) {
        setAiProfile(data.ai_profile);
        setWebsiteUrlInput(data.website_url || data.website || "");
        setGoogleBusinessInput(data.google_business_url || "");
        // Set selected images (default to first 4 or user's selection)
        const savedSelection = data.ai_profile.selected_image_indices || 
          (data.ai_profile.scraped_images?.slice(0, 4).map((_, i) => i) || []);
        setSelectedImages(savedSelection);
      }
      
      setCredentials(data.credentials || []);
      
      // Calculate compliance status
      calculateComplianceStatus(data.credentials || []);
      
      // Check if welcome banner should show
      const dismissed = localStorage.getItem("contractor_welcome_dismissed");
      setShowWelcomeBanner(!dismissed && (!data.company_logo || !data.credentials?.length));
      
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("contractor_token");
        navigate("/contractor/login");
      } else {
        toast.error("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Generate AI Profile
  const generateAIProfile = async () => {
    if (!websiteUrlInput) {
      toast.error("Please enter your website URL");
      return;
    }
    
    setGeneratingAI(true);
    try {
      const res = await axios.post(`${API}/contractor/profile/generate-ai`, {
        website_url: websiteUrlInput,
        google_business_url: googleBusinessInput
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiProfile(res.data.ai_profile);
      toast.success("AI profile generated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate AI profile");
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // Edit AI Profile
  const handleEditAiProfile = () => {
    setEditedCapabilities(aiProfile?.ai_capabilities_statement || "");
    setEditingAiProfile(true);
  };
  
  const handleSaveAiProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contractor/profile/ai`, {
        ai_capabilities_statement: editedCapabilities
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiProfile({ ...aiProfile, ai_capabilities_statement: editedCapabilities });
      setEditingAiProfile(false);
      toast.success("AI profile updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update AI profile");
    } finally {
      setSaving(false);
    }
  };
  
  // Delete AI Profile
  const handleDeleteAiProfile = async () => {
    if (!confirm("Are you sure you want to delete your AI-generated profile? This cannot be undone.")) {
      return;
    }
    
    setDeletingAiProfile(true);
    try {
      await axios.delete(`${API}/contractor/profile/ai`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiProfile(null);
      setWebsiteUrlInput("");
      setGoogleBusinessInput("");
      setSelectedImages([]);
      toast.success("AI profile deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete AI profile");
    } finally {
      setDeletingAiProfile(false);
    }
  };
  
  // Toggle image selection
  const toggleImageSelection = (index) => {
    setSelectedImages(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else if (prev.length < 4) {
        return [...prev, index];
      } else {
        toast.error("Maximum 4 images allowed");
        return prev;
      }
    });
  };
  
  // Save selected images
  const handleSaveSelectedImages = async () => {
    setSavingImages(true);
    try {
      await axios.put(`${API}/contractor/profile/ai/images`, {
        selected_image_indices: selectedImages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiProfile({ ...aiProfile, selected_image_indices: selectedImages });
      setEditingImages(false);
      toast.success("Image selection saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save image selection");
    } finally {
      setSavingImages(false);
    }
  };

  const calculateComplianceStatus = (creds) => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const expiringSoon = [];
    const expired = [];
    let hasValidCoi = false;
    let coiExpiry = null;
    let coiVerified = false;
    
    creds.forEach(cred => {
      if (cred.expiry_date) {
        const expiryDate = new Date(cred.expiry_date);
        if (expiryDate < today) {
          expired.push(cred);
        } else if (expiryDate < thirtyDaysFromNow) {
          expiringSoon.push(cred);
        }
      }
      
      if (cred.type === "coi") {
        const expiryDate = cred.expiry_date ? new Date(cred.expiry_date) : null;
        if (expiryDate && expiryDate > today) {
          hasValidCoi = true;
          coiExpiry = cred.expiry_date;
          coiVerified = cred.status === "approved" || cred.ai_verified;
        }
      }
    });
    
    const daysUntilCoiExpiry = coiExpiry ? 
      Math.ceil((new Date(coiExpiry) - today) / (1000 * 60 * 60 * 24)) : null;
    
    setCoiStatus({
      has_valid_coi: hasValidCoi,
      coi_expiry: coiExpiry,
      days_until_expiry: daysUntilCoiExpiry,
      is_verified: coiVerified
    });
    
    setComplianceStatus({
      is_discoverable: hasValidCoi && expired.length === 0,
      expiring_soon: expiringSoon,
      expired,
      all_valid: expired.length === 0
    });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contractor/profile/me`, {
        company_name: profile.company_name,
        contact_name: profile.contact_name,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        zip_code: profile.zip_code,
        website: profile.website,
        notes: profile.notes,
        company_logo: profile.company_logo,
        owner_photo: profile.owner_photo,
        expertise_tags: profile.expertise_tags,
        primary_category: profile.primary_category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, fieldType) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("field_type", fieldType);
    
    setUploadingFile(true);
    try {
      const res = await axios.post(`${API}/contractor/profile/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (fieldType === "company_logo") {
        setProfile({ ...profile, company_logo: res.data.url });
      } else if (fieldType === "owner_photo") {
        setProfile({ ...profile, owner_photo: res.data.url });
      } else if (fieldType === "credential") {
        setNewCredential({ ...newCredential, file_url: res.data.url });
      }
      
      toast.success("File uploaded successfully");
    } catch (err) {
      toast.error("Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleAddCredential = async () => {
    if (!newCredential.name) {
      toast.error("Credential name is required");
      return;
    }
    if (newCredential.type === "coi" && !newCredential.expiry_date) {
      toast.error("COI requires an expiration date");
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contractor/profile/credentials`, {
        credential_type: newCredential.type,
        credential_name: newCredential.name,
        issuing_body: newCredential.issuing_body,
        expiry_date: newCredential.expiry_date,
        coverage_amount: newCredential.coverage_amount ? parseFloat(newCredential.coverage_amount) : null,
        file_url: newCredential.file_url,
        notes: newCredential.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedCreds = [...credentials, res.data.credential];
      setCredentials(updatedCreds);
      calculateComplianceStatus(updatedCreds);
      
      setShowAddCredential(false);
      setNewCredential({
        type: "license",
        name: "",
        issuing_body: "",
        expiry_date: "",
        coverage_amount: "",
        file_url: "",
        notes: ""
      });
      toast.success("Credential added - pending AI verification");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add credential");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async (credId) => {
    if (!confirm("Are you sure you want to delete this credential?")) return;
    
    try {
      await axios.delete(`${API}/contractor/profile/credentials/${credId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedCreds = credentials.filter(c => c.id !== credId);
      setCredentials(updatedCreds);
      calculateComplianceStatus(updatedCreds);
      toast.success("Credential deleted");
    } catch (err) {
      toast.error("Failed to delete credential");
    }
  };

  const toggleExpertiseTag = (tagId) => {
    setProfile(prev => ({
      ...prev,
      expertise_tags: prev.expertise_tags.includes(tagId)
        ? prev.expertise_tags.filter(t => t !== tagId)
        : [...prev.expertise_tags, tagId]
    }));
  };

  const getCredentialStatus = (cred) => {
    if (!cred.expiry_date) return { status: "no_expiry", color: "bg-slate-100 text-slate-600", label: "No Expiry" };
    
    const today = new Date();
    const expiry = new Date(cred.expiry_date);
    const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return { status: "expired", color: "bg-red-100 text-red-700", label: "EXPIRED" };
    if (daysUntil <= 30) return { status: "expiring", color: "bg-amber-100 text-amber-700", label: `${daysUntil}d left` };
    return { status: "valid", color: "bg-emerald-100 text-emerald-700", label: "Valid" };
  };

  const dismissWelcomeBanner = () => {
    localStorage.setItem("contractor_welcome_dismissed", "true");
    setShowWelcomeBanner(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/contractor/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Welcome Hero Banner */}
        {showWelcomeBanner && (
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-6 mb-6 relative overflow-hidden" data-testid="contractor-welcome-banner">
            <button 
              onClick={dismissWelcomeBanner}
              className="absolute top-4 right-4 text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-6 h-6 text-amber-300" />
                <span className="text-emerald-100 font-medium">Your Expertise, Front & Center</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Become Fortress Certified
              </h2>
              <p className="text-emerald-100 mb-4 max-w-2xl">
                AskIsaac isn't just a portal—it's your gateway to the nation's leading health systems.
                To appear in &quot;One-Click Hire&quot; results for thousands of facility managers, 
                complete your profile and upload your credentials.
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg">
                  <Camera className="w-4 h-4 text-emerald-200" />
                  <span className="text-white">Add your logo & photo</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg">
                  <Shield className="w-4 h-4 text-emerald-200" />
                  <span className="text-white">Upload Licenses & COI</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg">
                  <Clock className="w-4 h-4 text-emerald-200" />
                  <span className="text-white">We track expirations</span>
                </div>
              </div>
            </div>
            {/* Background decoration */}
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white/5 rounded-full" />
            <div className="absolute right-20 top-0 w-20 h-20 bg-white/5 rounded-full" />
          </div>
        )}

        {/* Compliance Status Bar */}
        <div className={`rounded-xl p-4 mb-6 flex items-center justify-between ${
          complianceStatus.is_discoverable 
            ? "bg-emerald-50 border border-emerald-200" 
            : "bg-red-50 border border-red-200"
        }`} data-testid="compliance-status-bar">
          <div className="flex items-center gap-3">
            {complianceStatus.is_discoverable ? (
              <>
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-800">Fortress Certified</p>
                  <p className="text-sm text-emerald-600">Your profile is visible to hospitals</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-800">Profile Hidden</p>
                  <p className="text-sm text-red-600">
                    {!coiStatus.has_valid_coi 
                      ? "Upload a valid COI to become discoverable" 
                      : "Some credentials have expired"}
                  </p>
                </div>
              </>
            )}
          </div>
          
          {complianceStatus.expiring_soon.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>{complianceStatus.expiring_soon.length} expiring soon</span>
            </div>
          )}
        </div>

        {/* Profile Header Card */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
          {/* Cover/Header Area */}
          <div className="h-32 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 relative">
            {/* Verified Badge */}
            {coiStatus.is_verified && (
              <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-medium">
                <BadgeCheck className="w-4 h-4" />
                Verified
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 pb-6 relative">
            {/* Avatars row — overlap the cover band */}
            <div className="flex items-start justify-between -mt-12 mb-4">
              {/* Company Logo */}
              <div className="relative shrink-0">
                <div className="w-24 h-24 bg-white rounded-xl border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
                  {profile.company_logo ? (
                    <img src={profile.company_logo} alt="Company Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-10 h-10 text-slate-400" />
                  )}
                </div>
                <label className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-600 hover:bg-emerald-700 rounded-full flex items-center justify-center cursor-pointer transition-colors shadow">
                  <Camera className="w-4 h-4 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], "company_logo")}
                    disabled={uploadingFile}
                  />
                </label>
              </div>

              {/* Owner Photo */}
              <div className="relative shrink-0 mt-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full overflow-hidden flex items-center justify-center border-4 border-white shadow-lg">
                  {profile.owner_photo ? (
                    <img src={profile.owner_photo} alt="Owner" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <label className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-slate-700 hover:bg-slate-800 rounded-full flex items-center justify-center cursor-pointer transition-colors shadow">
                  <Camera className="w-3 h-3 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], "owner_photo")}
                    disabled={uploadingFile}
                  />
                </label>
              </div>
            </div>

            {/* Company Info — sits cleanly below the avatars */}
            <div>
              <div className="flex items-center flex-wrap gap-3 mb-1">
                <h1 className="text-2xl font-bold text-slate-900 break-words">{profile.company_name || "Your Company"}</h1>
                {coiStatus.is_verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <p className="text-slate-500">{profile.contact_name}</p>

              {/* Expertise Tags Preview */}
              {profile.expertise_tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {profile.expertise_tags.slice(0, 4).map(tagId => {
                    const tag = EXPERTISE_CATEGORIES.find(c => c.id === tagId);
                    return tag ? (
                      <span key={tagId} className={`px-2 py-1 rounded-full text-xs font-medium ${tag.color}`}>
                        {tag.label}
                      </span>
                    ) : null;
                  })}
                  {profile.expertise_tags.length > 4 && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      +{profile.expertise_tags.length - 4} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6 w-full justify-start overflow-x-auto">
            <TabsTrigger value="identity" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Identity
            </TabsTrigger>
            <TabsTrigger value="ai-profile" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              AI Profile
              {aiProfile && (
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
              )}
            </TabsTrigger>
            <TabsTrigger value="expertise" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Expertise
            </TabsTrigger>
            <TabsTrigger value="vault" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Credential Vault
              {complianceStatus.expired.length > 0 && (
                <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {complianceStatus.expired.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Identity Tab */}
          <TabsContent value="identity">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                Company Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={profile.company_name}
                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                    placeholder="Your Company LLC"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Name *</Label>
                  <Input
                    value={profile.contact_name}
                    onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email (Read-only)</Label>
                  <Input
                    value={profile.email}
                    disabled
                    className="mt-1 bg-slate-50"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="555-123-4567"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    placeholder="123 Main Street"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                    placeholder="Houston"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>State</Label>
                    <Input
                      value={profile.state}
                      onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                      placeholder="TX"
                      maxLength={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input
                      value={profile.zip_code}
                      onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                      placeholder="77001"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Website</Label>
                  <Input
                    value={profile.website}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>About / Notes</Label>
                  <Textarea
                    value={profile.notes}
                    onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                    placeholder="Brief description of your company and services..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* AI Profile Tab */}
          <TabsContent value="ai-profile">
            <div className="space-y-6">
              {/* AI Profile Generator */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">AI-Powered Profile</h2>
                    <p className="text-slate-500 text-sm">
                      Let AI generate a professional capabilities statement from your website
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Website URL</Label>
                    <div className="relative mt-1">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={websiteUrlInput}
                        onChange={(e) => setWebsiteUrlInput(e.target.value)}
                        placeholder="https://yourcompany.com"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      We&apos;ll scan your website to generate a professional summary
                    </p>
                  </div>
                  <div>
                    <Label>Google Business Profile (Optional)</Label>
                    <div className="relative mt-1">
                      <Star className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={googleBusinessInput}
                        onChange={(e) => setGoogleBusinessInput(e.target.value)}
                        placeholder="https://g.page/your-business"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Link your Google reviews for social proof
                    </p>
                  </div>
                </div>
                
                <Button 
                  onClick={generateAIProfile} 
                  disabled={generatingAI || !websiteUrlInput}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="generate-ai-profile-btn"
                >
                  {generatingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Scanning Website...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Profile
                    </>
                  )}
                </Button>
              </div>
              
              {/* AI Generated Content */}
              {aiProfile ? (
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-200 p-6" data-testid="ai-profile-content">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-violet-600" />
                      <h3 className="font-semibold text-violet-800">AI-Generated Profile</h3>
                      <span className="text-xs text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                        Generated {new Date(aiProfile.ai_generated_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleEditAiProfile}
                        className="text-violet-600 border-violet-300 hover:bg-violet-100"
                        data-testid="edit-ai-profile-btn"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDeleteAiProfile}
                        disabled={deletingAiProfile}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="delete-ai-profile-btn"
                      >
                        {deletingAiProfile ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-1" />
                        )}
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {/* Scraped Images from Website */}
                  {aiProfile.scraped_images?.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-violet-700 flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Website Images {editingImages ? "(Select up to 4)" : "(Verification)"}
                        </Label>
                        {!editingImages ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingImages(true)}
                            className="text-violet-600 border-violet-300"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Choose Images
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              onClick={handleSaveSelectedImages}
                              disabled={savingImages || selectedImages.length === 0}
                              className="bg-violet-600 hover:bg-violet-700"
                            >
                              {savingImages ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                              Save ({selectedImages.length}/4)
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setEditingImages(false);
                                setSelectedImages(aiProfile.selected_image_indices || [0, 1, 2, 3]);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {editingImages ? (
                        <>
                          <p className="text-xs text-violet-500 mb-3">
                            Click images to select/deselect. Selected images will be displayed on your public profile.
                          </p>
                          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                            {aiProfile.scraped_images.slice(0, 12).map((img, idx) => (
                              <div 
                                key={idx} 
                                onClick={() => toggleImageSelection(idx)}
                                className={`aspect-square rounded-lg overflow-hidden border-2 bg-white shadow-sm cursor-pointer transition-all relative ${
                                  selectedImages.includes(idx) 
                                    ? "border-emerald-500 ring-2 ring-emerald-200" 
                                    : "border-slate-200 hover:border-violet-300"
                                }`}
                              >
                                <img 
                                  src={img.url} 
                                  alt={img.alt || `Website image ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                />
                                {selectedImages.includes(idx) && (
                                  <div className="absolute top-2 right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                  </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-1">
                                  <span className="text-xs text-white font-medium">#{idx + 1}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {(selectedImages.length > 0 
                            ? selectedImages.map(idx => aiProfile.scraped_images[idx]).filter(Boolean)
                            : aiProfile.scraped_images.slice(0, 4)
                          ).map((img, idx) => (
                            <div 
                              key={idx} 
                              className="aspect-square rounded-lg overflow-hidden border-2 border-violet-200 bg-white shadow-sm hover:shadow-md transition-shadow"
                            >
                              <img 
                                src={img.url} 
                                alt={img.alt || `Website image ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {!editingImages && (
                        <p className="text-xs text-violet-500 mt-2">
                          Images from your website displayed for verification
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Capabilities Statement */}
                  <div className="mb-6">
                    <Label className="text-violet-700 mb-2 block">Capabilities Statement</Label>
                    {editingAiProfile ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editedCapabilities}
                          onChange={(e) => setEditedCapabilities(e.target.value)}
                          rows={4}
                          className="bg-white border-violet-300"
                          placeholder="Enter your capabilities statement..."
                        />
                        <div className="flex gap-2">
                          <Button 
                            onClick={handleSaveAiProfile}
                            disabled={saving}
                            className="bg-violet-600 hover:bg-violet-700"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                            Save Changes
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setEditingAiProfile(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg p-4 border border-violet-200">
                        <p className="text-slate-700 leading-relaxed">
                          {aiProfile.ai_capabilities_statement}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* AI-Detected Specialties */}
                  {aiProfile.ai_specialties?.length > 0 && (
                    <div className="mb-6">
                      <Label className="text-violet-700 mb-2 block">Detected Specialties</Label>
                      <div className="flex flex-wrap gap-2">
                        {aiProfile.ai_specialties.map((specialty, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1.5 bg-white border border-violet-200 rounded-full text-sm text-violet-700"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Source Links */}
                  <div className="flex flex-wrap gap-4 pt-4 border-t border-violet-200">
                    {aiProfile.website_url && (
                      <a 
                        href={aiProfile.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800"
                      >
                        <Globe className="w-4 h-4" />
                        Website
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {aiProfile.google_business_url && (
                      <a 
                        href={aiProfile.google_business_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800"
                      >
                        <Star className="w-4 h-4" />
                        Google Reviews
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
                  <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-slate-700 font-medium mb-2">No AI Profile Generated</h3>
                  <p className="text-slate-500 text-sm max-w-md mx-auto">
                    Enter your website URL above and click &quot;Generate AI Profile&quot; to create 
                    a professional capabilities statement that hospitals will see when searching for contractors.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Expertise Tab */}
          <TabsContent value="expertise">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Tag className="w-5 h-5 text-emerald-600" />
                Expertise & Categories
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Select all areas that match your expertise. These tags help hospitals find you when broadcasting service requests.
              </p>
              
              {/* Primary Category */}
              <div className="mb-6">
                <Label className="text-slate-700 mb-2 block">Primary Specialty</Label>
                <Select 
                  value={profile.primary_category} 
                  onValueChange={(val) => setProfile({ ...profile, primary_category: val })}
                >
                  <SelectTrigger className="w-full md:w-80">
                    <SelectValue placeholder="Select your primary specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERTISE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tag Cloud */}
              <div>
                <Label className="text-slate-700 mb-3 block">Additional Expertise (Select all that apply)</Label>
                <div className="flex flex-wrap gap-3">
                  {EXPERTISE_CATEGORIES.map(cat => {
                    const isSelected = profile.expertise_tags.includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => toggleExpertiseTag(cat.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isSelected 
                            ? `${cat.color} ring-2 ring-offset-2 ring-emerald-500` 
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`expertise-tag-${cat.id}`}
                      >
                        {isSelected && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Selected Tags Summary */}
              {profile.expertise_tags.length > 0 && (
                <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-emerald-800 font-medium text-sm mb-2">
                    {profile.expertise_tags.length} expertise area{profile.expertise_tags.length > 1 ? 's' : ''} selected
                  </p>
                  <p className="text-emerald-600 text-sm">
                    When a hospital broadcasts for these services, your profile will appear in their results.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Credential Vault Tab */}
          <TabsContent value="vault">
            <div className="space-y-6">
              {/* COI Gatekeeper Alert */}
              {!coiStatus.has_valid_coi && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-4" data-testid="coi-gatekeeper-alert">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800">COI Required</h3>
                    <p className="text-red-600 text-sm mt-1">
                      Your Certificate of Insurance (COI) is required to be visible to hospitals. 
                      Upload a valid COI to unlock broadcast visibility.
                    </p>
                  </div>
                  <Button 
                    onClick={() => { setNewCredential({ ...newCredential, type: "coi" }); setShowAddCredential(true); }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload COI
                  </Button>
                </div>
              )}
              
              {/* Credential Cards */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Credential Vault
                    </h2>
                    <p className="text-slate-500 text-sm">Licenses, certifications, and insurance documents</p>
                  </div>
                  <Button onClick={() => setShowAddCredential(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Credential
                  </Button>
                </div>
                
                {credentials.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                    <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-slate-700 font-medium mb-2">No credentials uploaded</h3>
                    <p className="text-slate-500 text-sm mb-4">
                      Add your licenses, certifications, and insurance to become verified
                    </p>
                    <Button variant="outline" onClick={() => setShowAddCredential(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Credential
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {credentials.map(cred => {
                      const statusInfo = getCredentialStatus(cred);
                      const isCOI = cred.type === "coi";
                      
                      return (
                        <div 
                          key={cred.id} 
                          className={`border rounded-xl p-4 transition-all hover:shadow-md ${
                            statusInfo.status === "expired" 
                              ? "border-red-200 bg-red-50/50" 
                              : statusInfo.status === "expiring"
                              ? "border-amber-200 bg-amber-50/50"
                              : "border-slate-200 bg-white"
                          }`}
                          data-testid={`credential-card-${cred.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isCOI ? "bg-violet-100" : "bg-emerald-100"
                              }`}>
                                {isCOI ? (
                                  <Shield className={`w-5 h-5 ${statusInfo.status === "expired" ? "text-red-600" : "text-violet-600"}`} />
                                ) : (
                                  <FileText className={`w-5 h-5 ${statusInfo.status === "expired" ? "text-red-600" : "text-emerald-600"}`} />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{cred.name}</p>
                                <p className="text-sm text-slate-500 capitalize">{cred.type?.replace("_", " ")}</p>
                                {cred.issuing_body && (
                                  <p className="text-xs text-slate-400 mt-1">{cred.issuing_body}</p>
                                )}
                              </div>
                            </div>
                            <button 
                              onClick={() => handleDeleteCredential(cred.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Expiry & Status */}
                          <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {cred.expiry_date && (
                                <span className="flex items-center gap-1 text-xs text-slate-500">
                                  <Calendar className="w-3 h-3" />
                                  Exp: {new Date(cred.expiry_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {cred.ai_verified && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  <Zap className="w-3 h-3" /> AI Verified
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </div>
                          </div>
                          
                          {/* Countdown Warning */}
                          {statusInfo.status === "expiring" && (
                            <div className="mt-3 p-2 bg-amber-100 rounded-lg text-amber-700 text-xs flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              <span>This credential expires soon - update it to maintain visibility</span>
                            </div>
                          )}
                          {statusInfo.status === "expired" && (
                            <div className="mt-3 p-2 bg-red-100 rounded-lg text-red-700 text-xs flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              <span>EXPIRED - Your profile is hidden until this is renewed</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-emerald-600" />
                Security Settings
              </h2>
              
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Password</p>
                    <p className="text-sm text-slate-500">Change your account password</p>
                  </div>
                  <Button variant="outline" onClick={() => setShowPasswordModal(true)}>
                    Change Password
                  </Button>
                </div>
              </div>
              
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Account Security Tips</p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• Use a strong password with at least 8 characters</li>
                      <li>• Never share your password with anyone</li>
                      <li>• Enable Telegram notifications for security alerts</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Credential Modal */}
      <Dialog open={showAddCredential} onOpenChange={setShowAddCredential}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              Add Credential
            </DialogTitle>
            <DialogDescription>
              Upload a license, certification, or insurance document
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select 
                value={newCredential.type}
                onValueChange={(val) => setNewCredential({ ...newCredential, type: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREDENTIAL_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Name / Description *</Label>
              <Input
                value={newCredential.name}
                onChange={(e) => setNewCredential({ ...newCredential, name: e.target.value })}
                placeholder="e.g., Master Plumber License #12345"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Issuing Body</Label>
              <Input
                value={newCredential.issuing_body}
                onChange={(e) => setNewCredential({ ...newCredential, issuing_body: e.target.value })}
                placeholder="e.g., Texas State Board"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expiration Date {newCredential.type === "coi" && "*"}</Label>
                <Input
                  type="date"
                  value={newCredential.expiry_date}
                  onChange={(e) => setNewCredential({ ...newCredential, expiry_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              {newCredential.type === "coi" && (
                <div>
                  <Label>Coverage Amount ($)</Label>
                  <Input
                    type="number"
                    value={newCredential.coverage_amount}
                    onChange={(e) => setNewCredential({ ...newCredential, coverage_amount: e.target.value })}
                    placeholder="1000000"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* File Upload */}
            <div>
              <Label>Document (PDF/Image)</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-emerald-400 transition-colors">
                {newCredential.file_url ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-600">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm">File uploaded</span>
                    <button 
                      onClick={() => setNewCredential({ ...newCredential, file_url: "" })}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG up to 10MB</p>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], "credential")}
                      disabled={uploadingFile}
                    />
                  </label>
                )}
              </div>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newCredential.notes}
                onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
                placeholder="Additional details..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCredential(false)}>Cancel</Button>
            <Button onClick={handleAddCredential} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Credential
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type={showPasswords ? "text" : "password"}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                placeholder="Minimum 8 characters"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type={showPasswords ? "text" : "password"}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                placeholder="Re-enter new password"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button 
              disabled={!passwords.current || !passwords.new || passwords.new !== passwords.confirm}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
